import type { CustomerwebSchemas } from "api-types";
import type { ReactNode } from "react";

import { BasiqClient } from "../elements/basiq/basiq-client";
import { useBankConnectWithPlaid } from "../elements/use-bank-connect";
import { useBankConnectionLogoResolvers, useBankConnectionLogos } from "../elements/use-bank-connect-logos";

export interface BankConnectProps extends CustomerwebSchemas.PlaidBankConnectSchema {
  basiqClient?: BasiqClient;
  customerSlug: string;
}

export interface PlaidBankConnectHookFactoryOptions {
  minBanksConnectedCallback?: () => void;
  onConnectionCallback?: () => void;
  onReconnectionCallback?: () => void;
  onReconnectionFailedCallback?: () => void;
  messageSuccess?: (message: string) => void;
  messageError?: (message: ReactNode) => void;
  onPlaidOpenCallback?: () => void;
  onPlaidExitCallback?: (metadata?: any) => void;
}

const minBanksConnected = 1;

export function plaidBankConnectHookFactory(
  bankConnectProps: BankConnectProps,
  options: PlaidBankConnectHookFactoryOptions = {}
) {
  const {
    minBanksConnectedCallback,
    onConnectionCallback,
    onReconnectionCallback,
    onReconnectionFailedCallback,
    messageSuccess,
    messageError,
    onPlaidOpenCallback,
    onPlaidExitCallback,
  } = options;
  return function useBankConnect() {
    const {
      plaidLinkRedirectUri,
      customerSlug,
      bankConnections: initialBankAccountConnectors = [],
      basiqClient,
    } = bankConnectProps;

    /**
     * plaid link handling
     */
    const { bankConnections, initiateLinkMode, initiateUpdateMode, plaidLinkElement } = useBankConnectWithPlaid({
      bankConnections: initialBankAccountConnectors,
      plaidLinkRedirectUri,
      customerSlug,
      onPlaidOpen: onPlaidOpenCallback,
      onPlaidExit: onPlaidExitCallback,
      onAccountConnected: onConnectionCallback,
      onAccountReconnected: onReconnectionCallback,
      onAccountReconnectFailed: onReconnectionFailedCallback,
      messageSuccess,
      messageError,
    });

    if (bankConnections.length >= minBanksConnected) {
      minBanksConnectedCallback && minBanksConnectedCallback();
    }

    const logoResolvers = useBankConnectionLogoResolvers({ customerSlug, basiqClient });

    const { logoLoadingStates } = useBankConnectionLogos({
      bankConnections,
      resolvers: logoResolvers,
    });

    return {
      bankConnections,
      startConnect: initiateLinkMode,
      startReconnect: initiateUpdateMode,
      logoLoadingStates,
      providerName: "Plaid",
      plaidLinkElement,
    };
  };
}
